/**
 * Notificações push no aparelho (navegador ou app instalado).
 * A assinatura vive no navegador (PushManager); a API guarda endpoint + chaves por pessoa (/app/push/assinaturas).
 * O estado local (permissão + assinatura deste aparelho) fica numa consulta do react-query, para o sino e a
 * página Empresa verem a mesma coisa.
 */
import type { NOTIFICACAO_TIPOS, PushAssinatura } from '@licita/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from './api';
import { ehIOS, instalado } from './instalar';
import { queryClient } from './queries';

export type PushTipo = (typeof NOTIFICACAO_TIPOS)[number];
export type PushAssinatura = ReturnType<typeof PushAssinatura.parse>;

/** Rótulos dos tipos, na ordem em que aparecem na tela. */
export const PUSH_TIPOS: { id: PushTipo; label: string; descricao: string }[] = [
  { id: 'edital_aprovado', label: 'Novo edital selecionado', descricao: 'A equipe aprovou um edital para a empresa.' },
  { id: 'comentario', label: 'Resposta da equipe', descricao: 'Mensagem nova na conversa de um edital.' },
  { id: 'etapa', label: 'Mudança de etapa', descricao: 'Um edital avançou no quadro.' },
  { id: 'anexo', label: 'Anexo', descricao: 'Arquivo novo no painel de um edital.' },
];
export const PUSH_TIPOS_TODOS: PushTipo[] = PUSH_TIPOS.map((t) => t.id);

/** Por que não deu para ativar; a tela traduz cada motivo numa orientação. */
export type PushMotivo = 'sem-suporte' | 'ios-nao-instalado' | 'sem-chave' | 'negado' | 'falha';
export class PushErro extends Error {
  constructor(
    public motivo: PushMotivo,
    message: string,
  ) {
    super(message);
  }
}

/** O navegador tem service worker, push e Notification? (no iPhone só depois de instalado na Tela de Início) */
export const suporte = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export interface PushEstado {
  suporte: boolean;
  permissao: NotificationPermission | 'indisponivel';
  /** Assinatura deste navegador, se houver (endpoint identifica o aparelho na lista da API). */
  endpoint: string | null;
}

let registro: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registra /sw.js uma vez (idempotente); devolve null onde não há service worker. */
export function registrar(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  registro ??= navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => null);
  return registro;
}

const registroPronto = async (): Promise<ServiceWorkerRegistration> => {
  const r = await registrar();
  if (!r) throw new PushErro('sem-suporte', 'Este navegador não aceita notificações no aparelho.');
  return navigator.serviceWorker.ready;
};

/** Permissão + assinatura deste navegador. */
export async function estado(): Promise<PushEstado> {
  if (!suporte()) return { suporte: false, permissao: 'indisponivel', endpoint: null };
  const r = await registrar();
  const sub = r ? await r.pushManager.getSubscription().catch(() => null) : null;
  return { suporte: true, permissao: Notification.permission, endpoint: sub?.endpoint ?? null };
}

export const pushEstadoQuery = queryOptions({ queryKey: ['push', 'estado'], queryFn: estado, staleTime: Infinity });

/** Chave pública VAPID; null = push desligado no servidor (a tela esconde o recurso). */
export const pushChaveQuery = queryOptions({
  queryKey: ['push', 'chave'],
  queryFn: () => api<{ chavePublica: string | null }>('/app/push/chave'),
  staleTime: Infinity,
});

/** Aparelhos da pessoa (todas as assinaturas, inclusive de outros navegadores). */
export const pushAssinaturasQuery = queryOptions({
  queryKey: ['push', 'assinaturas'],
  queryFn: () => api<{ itens: PushAssinatura[] }>('/app/push/assinaturas'),
});

const guardarLista = (r: { itens: PushAssinatura[] }) => queryClient.setQueryData(pushAssinaturasQuery.queryKey, r);
const atualizarEstado = () => queryClient.invalidateQueries({ queryKey: pushEstadoQuery.queryKey });

/** "Chrome · Windows", "Safari · iPhone"… só para a pessoa reconhecer o aparelho na lista. */
export function rotuloAparelho(ua = navigator.userAgent): string {
  const nav = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /SamsungBrowser\//.test(ua)
        ? 'Samsung Internet'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Chrome\//.test(ua)
            ? 'Chrome'
            : /Safari\//.test(ua)
              ? 'Safari'
              : 'Navegador';
  const so = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Macintosh/.test(ua)
            ? 'macOS'
            : /CrOS/.test(ua)
              ? 'ChromeOS'
              : /Linux/.test(ua)
                ? 'Linux'
                : '';
  const base = so ? `${nav} · ${so}` : nav;
  return instalado() ? `${base} (app)` : base;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const enviar = async (sub: PushSubscription, tipos?: PushTipo[]) => {
  const j = sub.toJSON();
  if (!j.endpoint || !j.keys?.p256dh || !j.keys.auth) throw new PushErro('falha', 'O navegador não devolveu a assinatura completa.');
  const r = await api<{ itens: PushAssinatura[] }>('/app/push/assinaturas', {
    method: 'POST',
    json: { endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth }, tipos, aparelho: rotuloAparelho() },
  });
  guardarLista(r);
  return r.itens;
};

/**
 * Pede permissão, assina no navegador e registra na API. Lança `PushErro` com o motivo quando não dá
 * (sem suporte, iPhone sem instalar, push desligado no servidor, permissão negada).
 */
export async function ativar(tipos: PushTipo[] = PUSH_TIPOS_TODOS): Promise<PushAssinatura[]> {
  if (!suporte()) {
    if (ehIOS() && !instalado())
      throw new PushErro('ios-nao-instalado', 'No iPhone, adicione o app à Tela de Início para receber notificações.');
    throw new PushErro('sem-suporte', 'Este navegador não aceita notificações no aparelho.');
  }
  const { chavePublica } = await queryClient.fetchQuery(pushChaveQuery);
  if (!chavePublica) throw new PushErro('sem-chave', 'Notificações no aparelho não estão disponíveis no momento.');
  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') {
    await atualizarEstado();
    throw new PushErro('negado', 'O navegador está bloqueando notificações deste site.');
  }
  const reg = await registroPronto();
  let sub = await reg.pushManager.getSubscription();
  try {
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(chavePublica) });
  } catch (e) {
    throw new PushErro('falha', e instanceof Error && e.message ? e.message : 'Não foi possível assinar as notificações.');
  }
  try {
    return await enviar(sub, tipos);
  } finally {
    await atualizarEstado();
  }
}

/** Cancela a assinatura deste navegador e apaga na API. */
export async function desativar(): Promise<void> {
  const r = await registrar();
  const sub = r ? await r.pushManager.getSubscription().catch(() => null) : null;
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => false);
  try {
    await removerAssinatura(endpoint);
  } finally {
    await atualizarEstado();
  }
}

/** Troca os tipos deste aparelho (reenvia a mesma assinatura com a lista nova). */
export async function atualizarTipos(tipos: PushTipo[]): Promise<PushAssinatura[]> {
  const r = await registrar();
  const sub = r ? await r.pushManager.getSubscription().catch(() => null) : null;
  if (!sub) throw new PushErro('falha', 'Este aparelho não está recebendo notificações.');
  return enviar(sub, tipos);
}

/** Apaga uma assinatura pelo endpoint (outro aparelho da pessoa, ou este). */
export async function removerAssinatura(endpoint: string): Promise<void> {
  await api<{ ok: true }>('/app/push/assinaturas', { method: 'DELETE', json: { endpoint } });
  queryClient.setQueryData(pushAssinaturasQuery.queryKey, (atual) =>
    atual ? { itens: atual.itens.filter((a) => a.endpoint !== endpoint) } : atual,
  );
  await queryClient.invalidateQueries({ queryKey: pushAssinaturasQuery.queryKey });
}

/** Orientação para cada motivo de falha (texto da tela). */
export function explicarMotivo(motivo: PushMotivo, mensagem: string): string {
  switch (motivo) {
    case 'negado':
      return 'As notificações estão bloqueadas para este site. Para liberar, abra as permissões do site no navegador (o cadeado ao lado do endereço) e permita notificações; depois tente de novo.';
    case 'ios-nao-instalado':
      return 'No iPhone, adicione à Tela de Início para receber notificações: toque em Compartilhar e depois em “Adicionar à Tela de Início”. Abra o app por lá e ative aqui.';
    case 'sem-suporte':
      return 'Este navegador não aceita notificações no aparelho. Tente o Chrome, o Edge, o Firefox ou o Safari atualizados.';
    case 'sem-chave':
      return 'Notificações no aparelho não estão disponíveis no momento.';
    default:
      return mensagem || 'Não foi possível ativar as notificações. Tente de novo em instantes.';
  }
}
